import React, { useCallback, useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { Download, ChevronLeft, ChevronRight } from "lucide-react";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { maintenanceApi, OrgItem, StationDropdownItem } from "../../../api/maintenance";
import { maintAnalyticsApi as api } from "../../../api/maintenance/analytics";
import type {
  GroupBy, TxnAggregateRow, TxnDetailRow, WalletMovement, UserWalletBalance, CategorySummaryRow,
} from "../../../api/maintenance/analytics";
import SessionDetailDrawer, { SessionBasic } from "../sessions/SessionDetailDrawer";
import "../shared/maintenance-analytics.scss";
import { useNotice } from "../shared/useNotice";

// payment_transaction_types -> friendly label. Direction (credit/debit) is derived
// from the amount sign (the types have no direction column; sign is the source of truth).
const TYPE_LABEL: Record<string, string> = {
  "charging": "Charging",
  "wallet-recharge": "Recharge",
  "wallet-refund": "Wallet refund",
  "org-wallet-recharge": "Org recharge",
  "org-wallet-claim": "Org claim",
  "org-wallet-transfer": "Org transfer",
  "wallet-recharge-cb": "Cashback",
  "wallet-recharge-cb-refund": "Cashback reversal",
  "charging-refund": "Charging refund",
};
const typeLabel = (t: string | null) => (t ? TYPE_LABEL[t] || t : "—");

// Direction from CHARGING-ADDA's perspective (NOT the user's wallet sign):
// money IN to us / revenue earned = positive (green); money OUT back to users
// (refunds) or given away (cashback) = negative (red).
const TYPE_DIRECTION: Record<string, 1 | -1> = {
  "charging": 1,                    // user consumed our service = revenue for us
  "wallet-recharge": 1,             // customer pays money in
  "org-wallet-recharge": 1,         // org tops up the system
  "wallet-recharge-cb-refund": 1,   // a cashback clawed back = we recover it
  "wallet-refund": -1,              // money refunded back to the user
  "charging-refund": -1,            // a charge refunded back to the user
  "wallet-recharge-cb": -1,         // cashback we give away — reduces our revenue
  "org-wallet-claim": -1,           // reimbursement paid out to a user
  "org-wallet-transfer": -1,
};
// default to inflow(+) for any unmapped/new type
const isInflow = (t: string | null) => !(t && TYPE_DIRECTION[t] === -1);

const ymd = (d: Date) => d.toISOString().slice(0, 10);
const validDay = (d: string) => /^\d{4}-\d{2}-\d{2}$/.test(d);
// The API speaks UTC only. Interpret the picked day in the browser's local zone
// and send a true UTC instant, so the filter matches the user's local calendar day.
const isoStart = (d: string) => new Date(`${d}T00:00:00`).toISOString();
const isoEnd = (d: string) => new Date(`${d}T23:59:59`).toISOString();
const num = (v: number | null | undefined, d = 0) =>
  v == null ? "—" : v.toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });
const money = (v: number | null | undefined) => (v == null ? "—" : `₹${num(v, 2)}`);
const ts = (s: string | null) => (s ? new Date(s).toLocaleString("en-IN", { hour12: false }) : "—");

type Tab = "aggregate" | "detail" | "movements" | "wallets";
type PresetId = "7" | "30" | "90" | "";
const PRESETS: { id: PresetId; label: string }[] = [{ id: "7", label: "7d" }, { id: "30", label: "30d" }, { id: "90", label: "90d" }];
const GROUPS: { id: GroupBy; label: string }[] = [
  { id: "date", label: "By date" }, { id: "station", label: "By station" }, { id: "user", label: "By user" }, { id: "org", label: "By org" },
];

const TransactionsReport: React.FC = () => {
  const today = new Date();
  const [start, setStart] = useState(ymd(new Date(today.getTime() - 30 * 864e5)));
  const [end, setEnd] = useState(ymd(today));
  const [preset, setPreset] = useState<PresetId>("30");
  const [orgId, setOrgId] = useState<string>("");
  const [stationId, setStationId] = useState<string>("");
  const [tab, setTab] = useState<Tab>("aggregate");
  const [groupBy, setGroupBy] = useState<GroupBy>("date");
  const [minBalance, setMinBalance] = useState<string>("");

  const [orgs, setOrgs] = useState<OrgItem[]>([]);
  const [stations, setStations] = useState<StationDropdownItem[]>([]);
  const [error, setError] = useNotice("error", "");
  const [loading, setLoading] = useState(false);

  const [agg, setAgg] = useState<TxnAggregateRow[]>([]);
  const [detail, setDetail] = useState<TxnDetailRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [movements, setMovements] = useState<WalletMovement[]>([]);
  const [catSummary, setCatSummary] = useState<CategorySummaryRow[]>([]);
  const [wallets, setWallets] = useState<UserWalletBalance[]>([]);
  const [sessId, setSessId] = useState<number | null>(null);
  const [sessBasic, setSessBasic] = useState<SessionBasic | null>(null);

  const openSession = (r: TxnDetailRow) => {
    if (r.session_id == null) return;
    setSessBasic({ session_id: r.session_id, user_name: r.user_name, organization: r.organization, device: r.device_used, consumed_kwh: r.consumed_kwh });
    setSessId(r.session_id);
  };

  useEffect(() => {
    maintenanceApi.getOrgs().then((r) => setOrgs(r.data)).catch(() => {});
    maintenanceApi.getStationsDropdown().then((r) => setStations(r.data)).catch(() => {});
  }, []);

  const applyPreset = (id: PresetId) => {
    const now = new Date();
    const days = id === "7" ? 7 : id === "90" ? 90 : 30;
    setStart(ymd(new Date(now.getTime() - days * 864e5)));
    setEnd(ymd(now)); setPreset(id); setPage(1);
  };

  const baseParams = useCallback(() => {
    const p: Record<string, any> = { start: isoStart(start), end: isoEnd(end) };
    if (orgId) p.org_id = Number(orgId);
    if (stationId) p.station_id = Number(stationId);
    return p;
  }, [start, end, orgId, stationId]);

  const load = useCallback(async () => {
    if (tab !== "wallets" && (!validDay(start) || !validDay(end))) return; // wait for a complete date
    setLoading(true); setError("");
    try {
      if (tab === "aggregate") setAgg((await api.txnAggregate({ ...baseParams(), group_by: groupBy })).data);
      else if (tab === "detail") {
        const r = await api.txnDetail({ ...baseParams(), page, page_size: 50 });
        setDetail(r.data.items); setTotalPages(r.data.total_pages); setTotal(r.data.total);
      } else if (tab === "movements") {
        const p = baseParams(); delete p.station_id;
        const [mv, cat] = await Promise.all([api.rechargesRefunds(p), api.categorySummary(p)]);
        setMovements(mv.data); setCatSummary(cat.data);
      } else {
        const p: Record<string, any> = {};
        if (orgId) p.org_id = Number(orgId);
        if (minBalance !== "") p.min_balance = Number(minBalance);
        setWallets((await api.userBalances(p)).data);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, [tab, groupBy, page, baseParams, orgId, minBalance, start, end]);

  useEffect(() => { load(); }, [load]);

  const doExport = async () => {
    try {
      const res = await api.txnExport(baseParams());
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url; a.download = `transactions_${start}_${end}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Export failed");
    }
  };

  const aggChart = agg.slice(0, 30).map((r) => ({
    label: r.session_date || r.station_name || r.user_name || r.organization || "—",
    value: r.total_amount,
  })).reverse();

  const TABS: { id: Tab; label: string }[] = [
    { id: "aggregate", label: "Charging (grouped)" },
    { id: "detail", label: "Charging (detail)" },
    { id: "movements", label: "Recharges & refunds" },
    { id: "wallets", label: "User wallets" },
  ];

  const dimHead = groupBy === "date" ? "Date" : groupBy === "station" ? "Station" : groupBy === "user" ? "User" : "Organization";
  const dimVal = (r: TxnAggregateRow) => r.session_date || r.station_name || r.user_name || r.organization || "—";

  return (
    <MaintenanceLayout title="Transactions & Wallets (Maintenance)">
      <div className="ma">
        <div className="ma-tabs">
          {TABS.map((t) => <button key={t.id} className={`ma-tab ${tab === t.id ? "on" : ""}`} onClick={() => { setTab(t.id); setPage(1); }}>{t.label}</button>)}
        </div>

        <div className="ma-toolbar">
          {tab !== "wallets" && (
            <>
              <div className="ma-presets">
                {PRESETS.map((p) => <button key={p.id} className={`ma-preset ${preset === p.id ? "on" : ""}`} onClick={() => applyPreset(p.id)}>{p.label}</button>)}
              </div>
              <label className="ma-date">From<input type="date" value={start} max={end} onChange={(e) => { setStart(e.target.value); setPreset(""); }} /></label>
              <label className="ma-date">To<input type="date" value={end} min={start} onChange={(e) => { setEnd(e.target.value); setPreset(""); }} /></label>
            </>
          )}
          <label className="ma-select">Org
            <select value={orgId} onChange={(e) => { setOrgId(e.target.value); setPage(1); }}>
              <option value="">All orgs</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
          {(tab === "aggregate" || tab === "detail") && (
            <label className="ma-select">Station
              <select value={stationId} onChange={(e) => { setStationId(e.target.value); setPage(1); }}>
                <option value="">All stations</option>
                {stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
          )}
          {tab === "wallets" && (
            <label className="ma-select">Min balance
              <input type="number" style={{ width: 110 }} value={minBalance} placeholder="any" onChange={(e) => setMinBalance(e.target.value)} />
            </label>
          )}
          <span className="ma-spacer" />
          {(tab === "aggregate" || tab === "detail") && <button className="ma-btn primary" onClick={doExport}><Download size={14} /> Export CSV</button>}
        </div>

        {error && <div className="ma-error">{error}</div>}
        {tab === "aggregate" && (
          <div className="ma-note">Revenue here is <strong>actual charged</strong> (wallet debit, processed only). Groupings share one dataset.</div>
        )}

        {/* aggregate group_by toggle */}
        {tab === "aggregate" && (
          <div className="ma-toggles" style={{ marginBottom: 14 }}>
            {GROUPS.map((g) => <button key={g.id} className={`ma-tg ${groupBy === g.id ? "on" : ""}`} onClick={() => setGroupBy(g.id)}>{g.label}</button>)}
          </div>
        )}

        {loading ? <p className="ma-loading">Loading…</p> : (
          <>
            {tab === "aggregate" && (
              <div className="ma-grid">
                <div className="ma-card two-third">
                  <div className="ma-card-h"><h3>Revenue {groupBy === "date" ? "over time" : `by ${groupBy}`}</h3></div>
                  <div className="ma-card-b">
                    {aggChart.length ? (
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={aggChart} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" vertical={false} />
                          <XAxis dataKey="label" stroke="#8a92a1" fontSize={10} minTickGap={12} tickFormatter={(v) => String(v).slice(0, 10)} />
                          <YAxis stroke="#8a92a1" fontSize={11} width={52} tickFormatter={(v) => `₹${num(v)}`} />
                          <Tooltip formatter={(v: any) => [money(v), "Revenue"]} cursor={{ fill: "rgba(85,84,211,0.06)" }} />
                          <Bar dataKey="value" fill="#5554d3" radius={[4, 4, 0, 0]} maxBarSize={34} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <p className="ma-empty">No transactions in range</p>}
                  </div>
                </div>
                <div className="ma-card third">
                  <div className="ma-card-h"><h3>Totals</h3></div>
                  <div className="ma-card-b ma-kpis" style={{ gridTemplateColumns: "1fr" }}>
                    <div className="ma-kpi"><div className="ma-kpi-label">Sessions</div><div className="ma-kpi-value">{num(agg.reduce((a, r) => a + r.total_sessions, 0))}</div></div>
                    <div className="ma-kpi"><div className="ma-kpi-label">Energy</div><div className="ma-kpi-value">{num(agg.reduce((a, r) => a + r.consumed_kwh, 0), 1)}<span style={{ fontSize: 12 }}> kWh</span></div></div>
                    <div className="ma-kpi"><div className="ma-kpi-label">Revenue</div><div className="ma-kpi-value">{money(agg.reduce((a, r) => a + r.total_amount, 0))}</div></div>
                  </div>
                </div>
                <div className="ma-card">
                  <div className="ma-tablewrap">
                    <table className="ma-table">
                      <thead><tr><th>{dimHead}</th><th className="num">Sessions</th><th className="num">Energy kWh</th><th className="num">Revenue</th></tr></thead>
                      <tbody>
                        {agg.map((r, i) => (
                          <tr key={i}><td>{dimVal(r)}</td><td className="num">{num(r.total_sessions)}</td><td className="num">{num(r.consumed_kwh, 1)}</td><td className="num">{money(r.total_amount)}</td></tr>
                        ))}
                        {!agg.length && <tr><td colSpan={4} className="ma-empty">No transactions</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {tab === "detail" && (
              <div className="ma-card">
                <div className="ma-tablewrap">
                  <table className="ma-table">
                    <thead><tr><th>Date</th><th className="num">Session</th><th>User</th><th>Station / device</th><th className="num">kWh</th><th className="num">Rate</th><th className="num">Amount</th><th>State</th></tr></thead>
                    <tbody>
                      {detail.map((r) => (
                        <tr key={r.transaction_id} className={r.session_id ? "clickable" : ""} onClick={() => openSession(r)}>
                          <td>{ts(r.transaction_date)}</td>
                          <td className="num">{r.session_id ?? "—"}</td>
                          <td>{r.user_name || "—"}<div className="sub">{r.organization || ""}</div></td>
                          <td className="sub">{r.device_used || "—"}</td>
                          <td className="num">{num(r.consumed_kwh, 2)}</td>
                          <td className="num">{r.rate_kwh != null ? `₹${num(r.rate_kwh, 2)}` : "—"}</td>
                          <td className="num">{money(Math.abs(r.transaction_amount))}</td>
                          <td><span className={`ma-pill ${r.transaction_state === "processed" ? "ok" : "muted"}`}>{r.transaction_state || "—"}</span></td>
                        </tr>
                      ))}
                      {!detail.length && <tr><td colSpan={8} className="ma-empty">No transactions</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="ma-pagination">
                  <span>{num(total)} transactions</span>
                  <span className="ctl">
                    <button disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft size={15} /></button>
                    Page {page} / {totalPages || 1}
                    <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight size={15} /></button>
                  </span>
                </div>
              </div>
            )}

            {tab === "movements" && (
              <>
                {/* per-category totals — ChargingAdda's perspective (money in vs out) */}
                <div className="ma-note">
                  From ChargingAdda's side: <strong style={{ color: "#2e7d32" }}>green = money in / revenue</strong> (recharge, charging),
                  {" "}<strong style={{ color: "#c62828" }}>red = money out / given away</strong> (refunds, cashback).
                </div>
                <div className="ma-kpis">
                  {catSummary.map((c) => {
                    const inflow = isInflow(c.category);
                    return (
                      <div className="ma-kpi" key={c.category}>
                        <div className="ma-kpi-label">
                          {typeLabel(c.category)} <span className={`ma-pill ${inflow ? "ok" : "bad"}`}>{inflow ? "in" : "out"}</span>
                        </div>
                        <div className="ma-kpi-value" style={{ color: inflow ? "#2e7d32" : "#c62828" }}>
                          {inflow ? "+" : "−"}₹{num(c.abs_total, 0)}
                        </div>
                        <div className="ma-kpi-sub">{num(c.count)} txns</div>
                      </div>
                    );
                  })}
                  {!catSummary.length && <div className="ma-empty">No wallet movements in range</div>}
                </div>

                <div className="ma-card">
                  <div className="ma-card-h"><h3>Recharges &amp; refunds <span className="ma-hint">· non-charging wallet movements</span></h3></div>
                  <div className="ma-tablewrap">
                    <table className="ma-table">
                      <thead><tr><th>Date</th><th>User</th><th>Type</th><th className="num">Amount</th><th className="num">Balance after</th></tr></thead>
                      <tbody>
                        {movements.map((m) => {
                          const inflow = isInflow(m.transaction_type);
                          return (
                            <tr key={m.transaction_id}>
                              <td>{ts(m.transaction_date)}</td>
                              <td>{m.user_name || "—"}<div className="sub">{m.organization || ""}</div></td>
                              <td><span className={`ma-pill ${inflow ? "ok" : "bad"}`}>{typeLabel(m.transaction_type)}</span></td>
                              <td className="num" style={{ color: inflow ? "#2e7d32" : "#c62828" }}>{inflow ? "+" : "−"}₹{num(Math.abs(m.transaction_amount), 2)}</td>
                              <td className="num">{money(m.wallet_balance_after)}</td>
                            </tr>
                          );
                        })}
                        {!movements.length && <tr><td colSpan={5} className="ma-empty">No recharges or refunds in range</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {tab === "wallets" && (
              <div className="ma-card">
                <div className="ma-card-h"><h3>User wallet balances <span className="ma-hint">· lowest first, capped 1000</span></h3></div>
                <div className="ma-tablewrap">
                  <table className="ma-table">
                    <thead><tr><th>User</th><th>Phone</th><th>Organization</th><th className="num">Balance</th></tr></thead>
                    <tbody>
                      {wallets.map((w) => (
                        <tr key={w.wallet_id}>
                          <td>{w.user_name || `#${w.user_id}`}</td>
                          <td className="sub">{w.phone || "—"}</td>
                          <td>{w.organization || "—"}</td>
                          <td className="num" style={{ color: w.current_balance < 0 ? "#c62828" : undefined }}>{money(w.current_balance)}</td>
                        </tr>
                      ))}
                      {!wallets.length && <tr><td colSpan={4} className="ma-empty">No wallets</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <SessionDetailDrawer sessionId={sessId} fallback={sessBasic} onClose={() => setSessId(null)} />
    </MaintenanceLayout>
  );
};

export default TransactionsReport;
